import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Drawer, Tabs, Table, Tag, Space, Typography, Descriptions, Select,
  Input, Alert, Empty, Collapse, Spin,
} from "antd";
import { Button, Toast } from "../../ui";
import { useTheme } from "../../../contexts/ThemeContext";
import ExcelAiService from "../../../services/excelAiService";

const { Text, Paragraph } = Typography;

const SEVERIDADE = {
  2: { rotulo: "Não carregado", cor: "error", ordem: 0 },
  1: { rotulo: "Atenção", cor: "warning", ordem: 1 },
  0: { rotulo: "Informativa", cor: "default", ordem: 2 },
};

/** Ações que exigem escolha do usuário antes de reprocessar. As demais são "aceitar como está". */
const ACOES_COM_ESCOLHA = new Set([1, 2, 3, 4, 5, 6, 7]);

const rotuloSeveridade = (s) => SEVERIDADE[s]?.rotulo || "—";

/**
 * Detalhe de um processamento: o que aconteceu, o que a IA entendeu de cada arquivo e o que
 * precisa da decisão do usuário.
 *
 * A aba "Arquivos" é o que dá confiança no produto — é onde o cliente vê *por que* o número é
 * aquele: qual coluna virou qual campo, com que confiança.
 */
const ExcelAiRunDetailDrawer = ({ aberto, syncId, run, podeEditar, onFechar, onReprocessado }) => {
  const { theme } = useTheme();
  const [detalhe, setDetalhe] = useState(null);
  const [pendencias, setPendencias] = useState([]);
  const [carregando, setCarregando] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [decisoes, setDecisoes] = useState({});

  const runId = run?.runId;

  const carregar = useCallback(async () => {
    if (!aberto || !syncId || !runId) return;
    try {
      setCarregando(true);
      setDecisoes({});
      const [d, p] = await Promise.all([
        ExcelAiService.obterRun(syncId, runId),
        ExcelAiService.listarPendencias(syncId, runId),
      ]);
      setDetalhe(d);
      setPendencias(Array.isArray(p) ? p : []);
    } catch (erro) {
      console.error("Erro ao carregar detalhe do processamento:", erro);
      Toast.error("Não foi possível carregar o detalhe deste processamento.");
    } finally {
      setCarregando(false);
    }
  }, [aberto, syncId, runId]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const naoCarregadas = useMemo(
    () => pendencias.filter((p) => p.severidade === 2 && p.status === 0),
    [pendencias],
  );

  /** Basta uma decisão pronta: nada bloqueia a carga, então reprocessar é sempre permitido. */
  const podeReprocessar = useMemo(
    () => Object.values(decisoes).some((d) => d.pronta),
    [decisoes],
  );

  /**
   * O que a tela envia é a ESCOLHA, não o payload. O formato do payload muda por tipo de pendência
   * e quem o monta é o integrador — fazer a UI adivinhar isso foi o que deixou as decisões
   * gravando sem produzir efeito nenhum no reprocessamento.
   */
  const definirDecisao = (pendencia, acao, valor) => {
    const precisaValor = ACOES_COM_ESCOLHA.has(acao);
    const pronta = !precisaValor || (valor !== undefined && valor !== null && valor !== "");
    setDecisoes((atual) => ({
      ...atual,
      [pendencia.id]: { pendenciaId: pendencia.id, acao, valor, pronta },
    }));
  };

  const aplicarEReprocessar = async () => {
    try {
      setSalvando(true);
      const resolucoes = Object.values(decisoes)
        .filter((d) => d.pronta)
        .map(({ pendenciaId, acao, valor }) => ({ pendenciaId, acao, valor }));
      await ExcelAiService.resolverPendencias(syncId, runId, resolucoes);

      // Gravar a decisão não reprocessa sozinho: quem tem os arquivos e sabe gerar um SAS novo é o
      // Backend, e o do processamento anterior já expirou. São duas chamadas de propósito.
      const resultado = await ExcelAiService.processar(syncId);

      if (resultado?.success) {
        Toast.success("Decisões registradas. Reprocessando os arquivos.");
      } else {
        Toast.info(
          resultado?.message ||
            "Decisões registradas. Inicie o processamento quando quiser.",
        );
      }

      onReprocessado?.();
      onFechar?.();
    } catch (erro) {
      console.error("Erro ao resolver pendências:", erro);
      Toast.error("Não foi possível registrar as decisões.");
    } finally {
      setSalvando(false);
    }
  };

  const colunasPendencia = [
    {
      title: "Severidade",
      dataIndex: "severidade",
      key: "severidade",
      width: 120,
      render: (s) => <Tag color={SEVERIDADE[s]?.cor}>{rotuloSeveridade(s)}</Tag>,
    },
    {
      title: "O que aconteceu",
      key: "mensagem",
      render: (_, p) => (
        <div>
          <Text style={{ fontSize: 13 }}>{p.mensagem}</Text>
          <div style={{ marginTop: 2 }}>
            <Text style={{ fontSize: 12, color: theme.textSecondary }}>
              {[p.arquivo, p.aba, p.coluna].filter(Boolean).join(" › ")}
              {p.ocorrencias > 1 && ` · ${p.ocorrencias.toLocaleString("pt-BR")} ocorrências`}
            </Text>
          </div>
        </div>
      ),
    },
    {
      title: "O que fazer",
      key: "acao",
      width: 280,
      render: (_, p) => {
        if (!podeEditar) return <Text style={{ color: theme.textSecondary }}>—</Text>;

        const opcoes = p.sugestoes || p.opcoes || [];
        const decisao = decisoes[p.id];

        // Pendência com opções fechadas (coluna, linha de DRE, dimensão): escolha num select,
        // com a sugestão da IA no topo e marcada como sugestão, não como verdade.
        if (opcoes.length > 0) {
          return (
            <Select
              size="small"
              style={{ width: "100%" }}
              placeholder="Escolher"
              value={decisao?.valor}
              onChange={(valor) => definirDecisao(p, p.acaoSugerida ?? 2, String(valor))}
              options={opcoes.map((o) => ({
                value: o.valor ?? o.value,
                label: o.confianca
                  ? `${o.rotulo ?? o.label} · sugestão (${Math.round(o.confianca * 100)}%)`
                  : (o.rotulo ?? o.label),
              }))}
            />
          );
        }

        if (p.severidade === 2) {
          return (
            <Input
              size="small"
              placeholder="Informar valor"
              value={decisao?.valor ?? ""}
              onChange={(e) => definirDecisao(p, p.acaoSugerida ?? 7, e.target.value)}
            />
          );
        }

        return (
          <Button
            size="small"
            type={decisao ? "primary" : "default"}
            onClick={() => definirDecisao(p, 11)}
          >
            {decisao ? "Aceito" : "Aceitar assim mesmo"}
          </Button>
        );
      },
    },
  ];

  const abaResumo = (
    <Descriptions column={1} size="small" bordered>
      <Descriptions.Item label="Status">{detalhe?.status || run?.status || "—"}</Descriptions.Item>
      <Descriptions.Item label="Estágio">{detalhe?.estagio || "—"}</Descriptions.Item>
      <Descriptions.Item label="Solicitado por">{detalhe?.solicitadoPor || "—"}</Descriptions.Item>
      <Descriptions.Item label="Registros carregados">
        {detalhe?.totalRegistros?.toLocaleString("pt-BR") || "—"}
      </Descriptions.Item>
      {detalhe?.resumoPorTabela && (
        <Descriptions.Item label="Por tabela">
          <Space wrap>
            {Object.entries(detalhe.resumoPorTabela).map(([tabela, qtd]) => (
              <Tag key={tabela}>
                {tabela}: {Number(qtd).toLocaleString("pt-BR")}
              </Tag>
            ))}
          </Space>
        </Descriptions.Item>
      )}
      {detalhe?.erroMensagem && (
        <Descriptions.Item label="Erro">
          <Text type="danger">{detalhe.erroMensagem}</Text>
        </Descriptions.Item>
      )}
    </Descriptions>
  );

  const abaArquivos = (detalhe?.arquivos || []).length === 0 ? (
    <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Sem detalhe de arquivos" />
  ) : (
    <Collapse
      accordion
      items={(detalhe.arquivos || []).map((arquivo) => ({
        key: arquivo.fileId || arquivo.nomeOriginal,
        label: (
          <Space wrap>
            <Text style={{ fontSize: 13, fontWeight: 500 }}>{arquivo.nomeOriginal}</Text>
            {/* O tipo é por aba, não por arquivo: uma planilha pode ter títulos numa aba e vendas
                em outra. Mostrar um tipo só para o arquivo inteiro seria mentira. */}
            {[...new Set((arquivo.abas || []).map((a) => a.tipoDetectado))].map((tipo) => (
              <Tag key={tipo}>{tipo || "Não classificado"}</Tag>
            ))}
            {(arquivo.abas || []).length === 0 && <Tag color="warning">{arquivo.status}</Tag>}
          </Space>
        ),
        children: (
          <>
            {(arquivo.abas || []).map((aba) => (
              <div key={aba.nomeAba} style={{ marginBottom: 12 }}>
                <Paragraph style={{ marginBottom: 4, fontSize: 13 }}>
                  <b>{aba.nomeAba}</b> · cabeçalho na linha {aba.linhaCabecalho ?? "?"} ·{" "}
                  {(aba.linhasAproveitadas ?? 0).toLocaleString("pt-BR")} de{" "}
                  {(aba.linhasLidas ?? 0).toLocaleString("pt-BR")} linhas aproveitadas
                </Paragraph>
                <Table
                  size="small"
                  rowKey={(c, i) => `${aba.nomeAba}-${c.origem}-${i}`}
                  pagination={false}
                  dataSource={aba.colunas || []}
                  columns={[
                    { title: "Coluna da planilha", dataIndex: "origem", key: "origem" },
                    {
                      title: "Vira",
                      dataIndex: "destino",
                      key: "destino",
                      render: (d) =>
                        d || <Text style={{ color: theme.textSecondary }}>não usada</Text>,
                    },
                    {
                      title: "Confiança",
                      dataIndex: "confianca",
                      key: "confianca",
                      width: 110,
                      render: (c) =>
                        c == null ? (
                          "—"
                        ) : (
                          <Text type={c < 0.7 ? "warning" : undefined}>
                            {Math.round(c * 100)}%
                          </Text>
                        ),
                    },
                  ]}
                />
              </div>
            ))}
          </>
        ),
      }))}
    />
  );

  const abaPendencias = (
    <>
      {naoCarregadas.length > 0 && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message={
            naoCarregadas.length === 1
              ? "1 item não pôde ser carregado"
              : `${naoCarregadas.length} itens não puderam ser carregados`
          }
          description="O restante dos dados foi carregado normalmente. Resolva abaixo e reprocesse para incluir o que faltou."
        />
      )}
      <Table
        size="small"
        rowKey="id"
        columns={colunasPendencia}
        dataSource={[...pendencias].sort(
          (a, b) => (SEVERIDADE[a.severidade]?.ordem ?? 9) - (SEVERIDADE[b.severidade]?.ordem ?? 9),
        )}
        pagination={false}
        locale={{
          emptyText: (
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Nenhuma pendência" />
          ),
        }}
      />
    </>
  );

  return (
    <Drawer
      open={aberto}
      onClose={onFechar}
      width={880}
      title="Detalhes do processamento"
      extra={
        podeEditar &&
        pendencias.some((p) => p.status === 0) && (
          <Button
            type="primary"
            loading={salvando}
            disabled={!podeReprocessar}
            onClick={aplicarEReprocessar}
            style={{ backgroundColor: theme.primary, borderColor: theme.primary }}
          >
            Aplicar e reprocessar
          </Button>
        )
      }
    >
      {carregando ? (
        <div style={{ textAlign: "center", padding: 48 }}>
          <Spin />
        </div>
      ) : (
        <Tabs
          items={[
            { key: "resumo", label: "Resumo", children: abaResumo },
            { key: "arquivos", label: "Arquivos", children: abaArquivos },
            {
              key: "pendencias",
              label: `Pendências${pendencias.length ? ` (${pendencias.length})` : ""}`,
              children: abaPendencias,
            },
          ]}
        />
      )}
    </Drawer>
  );
};

export default ExcelAiRunDetailDrawer;
